import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check, Lock, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";

const BackDialog = ({ open, onOpenChange, r, meta, onDone }) => {
  const earlier = meta.stages.slice(0, r.stage_index);
  const [to, setTo] = useState("");
  const [comment, setComment] = useState("");
  const submit = async () => {
    try {
      await api.post(`/rights/${r.id}/advance`, { to_stage: to, comment });
      toast.success(`Moved back to ${meta.stage_labels[to]}`);
      onOpenChange(false); setComment(""); setTo("");
      onDone();
    } catch (e) { toast.error(apiError(e, "Could not move back")); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="stage-back-dialog">
        <DialogHeader>
          <DialogTitle>Move request back</DialogTitle>
          <DialogDescription>Give a reason. It is recorded in the audit trail.</DialogDescription>
        </DialogHeader>
        <Select value={to} onValueChange={setTo}>
          <SelectTrigger data-testid="stage-back-select"><SelectValue placeholder="Choose stage" /></SelectTrigger>
          <SelectContent>{earlier.map((s) => <SelectItem key={s} value={s}>{meta.stage_labels[s]}</SelectItem>)}</SelectContent>
        </Select>
        <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Reason" data-testid="stage-back-comment" />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!to || !comment.trim()} onClick={submit} data-testid="stage-back-submit">Move back</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const StageStepper = ({ r, meta, canEdit, pendingApproval, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [back, setBack] = useState(false);
  const next = r.next_stage;
  const blockers = next ? r.checklist.gates[next] : [];

  const advance = async () => {
    setBusy(true);
    try {
      await api.post(`/rights/${r.id}/advance`, { to_stage: next });
      toast.success(`Moved to ${meta.stage_labels[next]}`);
      onChanged();
    } catch (e) { toast.error(apiError(e, "Could not advance")); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="stage-stepper">
      <ol className="grid grid-cols-6 gap-1.5">
        {meta.stages.map((s, i) => {
          const done = i < r.stage_index || r.status === "CLOSED";
          const cur = i === r.stage_index && r.status !== "CLOSED";
          return (
            <li key={s} data-testid={`stage-step-${s.toLowerCase()}`} data-state={done ? "done" : cur ? "current" : "todo"}>
              <div className={`h-1.5 rounded-full transition-colors duration-500 ${done ? "bg-[#003366]" : cur ? "bg-[#008080]" : "bg-slate-200"}`} />
              <div className="flex items-center gap-1.5 mt-2">
                <span className={`w-5 h-5 rounded-full text-[10px] font-mono flex items-center justify-center shrink-0 ${
                  done ? "bg-[#003366] text-white" : cur ? "bg-[#008080] text-white ring-4 ring-blue-100" : "bg-slate-100 text-slate-400"}`}>
                  {done ? <Check className="w-3 h-3" /> : i + 1}
                </span>
                <span className={`text-[12px] font-semibold truncate ${cur ? "text-[#008080]" : done ? "text-[#003366]" : "text-slate-400"}`}>
                  {meta.stage_labels[s]}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
      {r.status !== "CLOSED" && canEdit && (
        <div className="mt-4 pt-4 border-t border-slate-100 flex items-start justify-between gap-4 flex-wrap">
          <div className="text-xs text-slate-600 min-w-0" data-testid="stage-blockers">
            {pendingApproval ? (
              <span className="flex items-center gap-1.5 text-purple-700"><Lock className="w-3.5 h-3.5" /> Closure approval pending, so the stage is locked.</span>
            ) : !next ? (
              <span>Final stage. Request closure approval, then close the request.</span>
            ) : blockers.length ? (
              <ul className="space-y-0.5">{blockers.map((b) => <li key={b} className="text-amber-700">• {b}</li>)}</ul>
            ) : (
              <span className="text-emerald-700">Ready to move to {meta.stage_labels[next]}.</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {r.stage_index > 0 && !pendingApproval && (
              <Button size="sm" variant="ghost" onClick={() => setBack(true)} data-testid="stage-back-button">
                <Undo2 className="w-3.5 h-3.5 mr-1" /> Move back
              </Button>
            )}
            {next && (
              <Button size="sm" onClick={advance} disabled={busy || blockers.length > 0 || !!pendingApproval}
                      className="bg-[#003366] hover:bg-[#003366]/90" data-testid="stage-advance-button">
                Move to {meta.stage_labels[next]} <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            )}
          </div>
        </div>
      )}
      <BackDialog open={back} onOpenChange={setBack} r={r} meta={meta} onDone={onChanged} />
    </div>
  );
};
